"use strict";

// src/index.ts
import { UserManager } from "oidc-client-ts";
import { unref as unref2 } from "vue";

// src/keys.ts
var OIDC_REDIRECT_URI = "OIDC_REDIRECT_URI";

// src/store/index.ts
import { useStorage } from "@vueuse/core";
import { computed, reactive } from "vue";
var storage = useStorage(OIDC_REDIRECT_URI, "");
var state = reactive({
  settings: null,
  oidcSettings: null,
  userManager: null,
  refreshUserManager: null,
  user: null,
  token: computed(() => {
    var _a;
    return ((_a = state.user) == null ? void 0 : _a.access_token) || null;
  }),
  hasExpiresAt: computed(() => {
    var _a;
    return Date.now() / 1e3 > ((_a = state.user) == null ? void 0 : _a.expires_at) || false;
  }),
  redirect_uri: storage.value
});
var actions = {
  setUser(user) {
    var _a;
    state.user = user;
    (_a = state.userManager) == null ? void 0 : _a.storeUser(user);
  },
  async removeUser() {
    var _a;
    state.user = null;
    await ((_a = state.userManager) == null ? void 0 : _a.removeUser());
  }
};
function useOidcStore() {
  return {
    state: computed(() => state),
    actions: computed(() => actions)
  };
}

// src/store/events.ts
var inlineOidcEvents = {
  addUserLoaded
};
function addUserLoaded(user) {
  console.log(user);
}

// src/useAuth.ts
import { toValue } from "@vueuse/core";
import { unref } from "vue";

// src/utils.ts
function isPathOfCallback(location2 = window.location) {
  let searchParams = new URLSearchParams(location2.search);
  if ((searchParams.get("code") || searchParams.get("error")) && searchParams.get("state")) {
    return true;
  }
  searchParams = new URLSearchParams(location2.hash.replace("#", "?"));
  if ((searchParams.get("code") || searchParams.get("error")) && searchParams.get("state")) {
    return true;
  }
  return false;
}

// src/useAuth.ts
var { state: state2, actions: actions2 } = useOidcStore();
function useAuth() {
  return {
    autoAuthenticate,
    signinRedirect,
    signoutRedirect,
    refreshToken
  };
}
function signinRedirect(arg) {
  var _a;
  if (!unref(state2).user) {
    (_a = unref(state2).userManager) == null ? void 0 : _a.signinRedirect(arg);
  }
}
function signoutRedirect(arg) {
  var _a;
  (_a = unref(state2).userManager) == null ? void 0 : _a.signoutRedirect(arg);
}
async function autoAuthenticate(uri = "") {
  var _a, _b, _c, _d, _e, _f, _g;
  let timer = null;
  const user = await ((_a = unref(state2).userManager) == null ? void 0 : _a.getUser()) || unref(state2).user;
  if (!user && !isPathOfCallback()) {
    storage.value = uri || location.pathname + location.search || "/";
    await ((_b = unref(state2).userManager) == null ? void 0 : _b.removeUser());
    await ((_c = unref(state2).userManager) == null ? void 0 : _c.signinRedirect());
    return;
  }
  if (!user && isPathOfCallback()) {
    const user2 = await ((_d = unref(state2).userManager) == null ? void 0 : _d.signinRedirectCallback());
    ((_e = unref(state2).oidcSettings) == null ? void 0 : _e.onSigninRedirectCallback) && ((_g = (_f = unref(state2).oidcSettings) == null ? void 0 : _f.onSigninRedirectCallback) == null ? void 0 : _g.call(_f, user2));
    unref(actions2).setUser(user2);
    return;
  }
  if (user && !isPathOfCallback()) {
    if (timer)
      clearInterval(timer);
    unref(actions2).setUser(user);
    useRefreshToken();
    if (unref(state2).hasExpiresAt) {
      await signoutRedirect();
      return;
    }
    return;
  }
  if (user && isPathOfCallback()) {
    timer = setInterval(() => {
      var _a2, _b2, _c2;
      ((_a2 = unref(state2).oidcSettings) == null ? void 0 : _a2.onSigninRedirectCallback) && ((_c2 = (_b2 = unref(state2).oidcSettings) == null ? void 0 : _b2.onSigninRedirectCallback) == null ? void 0 : _c2.call(_b2, user));
      unref(actions2).setUser(user);
    }, 3e3);
    return;
  }
}
function useRefreshToken() {
  var _a;
  const _config = (_a = toValue(state2).settings) == null ? void 0 : _a.refreshToken;
  if (_config == null ? void 0 : _config.enable) {
    setInterval(async () => {
      refreshToken();
    }, _config.time);
  }
}
function refreshToken(args, fn1, fn2) {
  const mgr = toValue(state2).refreshUserManager;
  mgr == null ? void 0 : mgr.signinSilent(args).then((res) => {
    res && unref(actions2).setUser(res);
    fn1 && fn1(res);
  }).catch((err) => {
    fn2 && fn2(err);
  });
}

// src/index.ts
export * from "oidc-client-ts";
var { state: state3, actions: actions3 } = useOidcStore();
var inlineCreateOidcOptions = {
  refreshToken: {
    enable: false,
    time: 3e3 * 10,
    settings: null
  }
};
function createOidc(options) {
  const _options = { ...inlineCreateOidcOptions, ...options };
  const { oidcSettings, auth, refreshToken: refreshToken2 } = _options;
  const events = { ...inlineOidcEvents, ...options.events };
  unref2(state3).settings = _options;
  unref2(state3).oidcSettings = oidcSettings;
  unref2(state3).userManager = new UserManager(oidcSettings);
  unref2(state3).refreshUserManager = (refreshToken2 == null ? void 0 : refreshToken2.settings) ? new UserManager(refreshToken2.settings) : null;
  Object.keys(events).forEach((key) => {
    unref2(state3).userManager.events[key](events[key]);
  });
  unref2(state3).userManager.getUser().then((user) => {
    actions3.value.setUser(user);
  });
  const { autoAuthenticate: autoAuthenticate2 } = useAuth();
  if (auth && autoAuthenticate2)
    autoAuthenticate2();
}
export {
  createOidc,
  storage,
  useAuth,
  useOidcStore,
  useRefreshToken
};
